/** Shared-company inbox / board authority. Nanode is source of truth after Push/Pull. */

export type CompanySyncKind = "same" | "orphan" | "empty-remote" | "empty-local" | "unknown";

export type CompanySyncClassification = {
  kind: CompanySyncKind;
  localId: string;
  remoteId: string;
  localName: string;
  remoteName: string;
};

export type InboxPublisherEvent =
  | "create"
  | "join"
  | "conflict-pull"
  | "overwrite-push"
  | "same-pull"
  | "resume-dispatch";

function trimId(raw?: string | null): string {
  return typeof raw === "string" ? raw.trim() : "";
}

function trimName(raw?: string | null): string {
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Same operator name is not enough — two first-run careers both suggest
 * "Celtic Air Services" and mint different `company.id`s.
 */
export function classifyCompanySync(input: {
  localCompanyId?: string | null;
  remoteCompanyId?: string | null;
  localName?: string | null;
  remoteName?: string | null;
}): CompanySyncClassification {
  const localId = trimId(input.localCompanyId);
  const remoteId = trimId(input.remoteCompanyId);
  const localName = trimName(input.localName);
  const remoteName = trimName(input.remoteName);
  if (!localId && !remoteId) return { kind: "unknown", localId, remoteId, localName, remoteName };
  if (!remoteId) return { kind: "empty-remote", localId, remoteId, localName, remoteName };
  if (!localId) return { kind: "empty-local", localId, remoteId, localName, remoteName };
  if (localId === remoteId) return { kind: "same", localId, remoteId, localName, remoteName };
  return { kind: "orphan", localId, remoteId, localName, remoteName };
}

/**
 * Create / confirmed overwrite = this PC is the ops publisher.
 * Join and any Pull (including same-company) follow Nanode — Book/satellite
 * must not stay a mint+auto-Push desk after Pull.
 */
export function inboxPublisherAfter(event: InboxPublisherEvent): boolean {
  return event === "create" || event === "overwrite-push" || event === "resume-dispatch";
}

export type DeskBoardSeat = "publisher" | "follower";

export function hydrateBoardSeat(
  raw?: string | null,
  inboxPublisher?: boolean,
): DeskBoardSeat {
  if (raw === "follower") return "follower";
  if (raw === "publisher") return "publisher";
  return inboxPublisher === false ? "follower" : "publisher";
}

/** Join / take-Nanode stick as follower. Create / confirmed overwrite is the sim desk. */
export function boardSeatAfter(event: InboxPublisherEvent, previous?: DeskBoardSeat | null): DeskBoardSeat {
  if (event === "join" || event === "conflict-pull") return "follower";
  if (event === "create" || event === "resume-dispatch") return "publisher";
  return previous === "follower" ? "follower" : "publisher";
}

export function applySyncAuthority(
  event: InboxPublisherEvent,
  previous?: { boardSeat?: DeskBoardSeat | null; inboxPublisher?: boolean; joinedRemote?: boolean },
): { inboxPublisher: boolean; boardSeat: DeskBoardSeat; joinedRemote: boolean } {
  const boardSeat = boardSeatAfter(event, hydrateBoardSeat(previous?.boardSeat, previous?.inboxPublisher));
  const joinedRemote =
    event === "join" || event === "conflict-pull"
      ? true
      : event === "create" || event === "overwrite-push" || event === "resume-dispatch"
        ? false
        : previous?.joinedRemote === true;
  return {
    boardSeat,
    inboxPublisher: boardSeat === "publisher" && inboxPublisherAfter(event),
    joinedRemote,
  };
}

/**
 * Same-PC Switch user (Dispatch ↔ Pilot) must not persist Join/follower.
 * Pilot role already fail-closes full-board Push. A real Join sets joinedRemote.
 */
export function syncAuthorityAfterRoleSwitch(input: {
  role?: string | null;
  boardSeat?: string | null;
  inboxPublisher?: boolean;
  joinedRemote?: boolean;
}): { inboxPublisher: boolean; boardSeat: DeskBoardSeat; joinedRemote: boolean } | null {
  const role = String(input.role ?? "").trim().toLowerCase();
  if (role === "dispatcher" && input.joinedRemote !== true) {
    return applySyncAuthority("resume-dispatch", {
      boardSeat: hydrateBoardSeat(input.boardSeat, input.inboxPublisher),
      inboxPublisher: input.inboxPublisher,
      joinedRemote: input.joinedRemote,
    });
  }
  return null;
}

export function shouldMintAutoInbox(input: {
  autoInboxEnabled: boolean;
  syncConfigured: boolean;
  inboxPublisher: boolean;
  companyKind: CompanySyncKind;
  /** Local desk already matches the Nanode schedule hash (or remote is empty). */
  remoteCaughtUp: boolean;
}): boolean {
  if (!input.autoInboxEnabled) return false;
  if (!input.syncConfigured) return true;
  if (input.companyKind === "orphan") return false;
  if (!input.inboxPublisher) return false;
  if (!input.remoteCaughtUp && input.companyKind !== "empty-remote") return false;
  return true;
}

export const PILOT_PUSH_BLOCKED =
  "SYNC  this desk follows the Dispatch company board — Pull only. A Push from here would overwrite Inbox / Trip Log / schedule. Sign on as Dispatch on the sim desk to publish.";

export const FOLLOWER_CLOBBER_BLOCKED =
  "SYNC  this desk’s local career would overwrite the Dispatch company board on Nanode. Pull the shared snapshot. A second desk cannot Push over Inbox / Trip Log / schedule.";

export const STALE_DESK_PUSH_BLOCKED =
  "SYNC  this desk is older or stale — a Push would clobber the Dispatch board. Update this app and Pull. Nanode / Dispatch stays the authority.";

export type BoardAuthority = "dispatch" | "follower";

export type BoardPushClass = "allow" | "follower" | "stale-desk" | "orphan-clobber";

function parseDeskSemver(raw?: string | null): [number, number, number] | null {
  const match = String(raw ?? "")
    .trim()
    .match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** Missing / unparseable local version is older — fail closed. */
export function compareDeskSemver(local?: string | null, remote?: string | null): number {
  const a = parseDeskSemver(local);
  const b = parseDeskSemver(remote);
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return a[i]! < b[i]! ? -1 : 1;
  }
  return 0;
}

export function boardAuthorityForDesk(input: {
  role?: string | null;
  inboxPublisher?: boolean;
  boardSeat?: string | null;
}): BoardAuthority {
  if (hydrateBoardSeat(input.boardSeat, input.inboxPublisher) === "follower") return "follower";
  const role = String(input.role ?? "").trim().toLowerCase();
  if (role === "pilot") return "follower";
  if (input.inboxPublisher === false) return "follower";
  return "dispatch";
}

/**
 * Nanode PUT /v1/state gate. Existing Dispatch board wins over a follower,
 * an older desk build, or a different local career (stale AppData).
 */
export function classifyBoardPush(input: {
  incomingAuthority?: string | null;
  incomingDeskVersion?: string | null;
  storedDeskVersion?: string | null;
  incomingCompanyId?: string | null;
  storedCompanyId?: string | null;
  remoteHasBoard?: boolean;
  overwrite?: boolean;
}): BoardPushClass {
  const authority = String(input.incomingAuthority ?? "").trim().toLowerCase();
  if (authority === "follower") return "follower";
  const remoteHasBoard = input.remoteHasBoard !== false;
  const storedVer = trimId(input.storedDeskVersion);
  const incomingVer = trimId(input.incomingDeskVersion);
  if (remoteHasBoard && storedVer && (!incomingVer || compareDeskSemver(incomingVer, storedVer) < 0)) {
    return "stale-desk";
  }
  const storedId = trimId(input.storedCompanyId);
  const incomingId = trimId(input.incomingCompanyId);
  if (remoteHasBoard && storedId && incomingId && storedId !== incomingId) {
    if (input.overwrite && authority === "dispatch") return "allow";
    return "orphan-clobber";
  }
  return "allow";
}

export function boardPushErrorMessage(code: BoardPushClass | string): string {
  if (code === "stale-desk" || code === "stale_desk") return STALE_DESK_PUSH_BLOCKED;
  if (code === "orphan-clobber" || code === "orphan_clobber") return FOLLOWER_CLOBBER_BLOCKED;
  return PILOT_PUSH_BLOCKED;
}

/**
 * Silent board Push. Orphans never auto-Push.
 * Only the Dispatch / ops desk (`inboxPublisher !== false`) publishes board
 * truth. Pilot / Join / Galaxy Book followers Pull — they must not Push a
 * second career over Emerson’s board even after a local edit.
 */
export function shouldAutoPushSchedule(input: {
  syncConfigured: boolean;
  companyKind: CompanySyncKind;
  inboxPublisher?: boolean;
  boardSeat?: string | null;
  role?: string | null;
  /** Ignored for followers — fail closed. Kept so existing callers type-check. */
  syncedBoardMutation?: boolean;
  /** Local start-from-scratch wipe — do not Push the empty board unless asked. */
  holdRemoteBoard?: boolean;
}): boolean {
  if (!input.syncConfigured) return false;
  if (input.holdRemoteBoard) return false;
  if (input.companyKind === "orphan") return false;
  if (boardAuthorityForDesk(input) === "follower") return false;
  return input.inboxPublisher !== false;
}

export function canPushCompanyBoard(input: {
  inboxPublisher?: boolean;
  role?: string | null;
  boardSeat?: string | null;
  companyKind?: CompanySyncKind;
  localDeskVersion?: string | null;
  remoteDeskVersion?: string | null;
  remoteHasBoard?: boolean;
  localCompanyId?: string | null;
  remoteCompanyId?: string | null;
}): { ok: true } | { ok: false; error: string } {
  if (boardAuthorityForDesk(input) === "follower") {
    return { ok: false, error: PILOT_PUSH_BLOCKED };
  }
  if (input.companyKind === "orphan") {
    return { ok: false, error: FOLLOWER_CLOBBER_BLOCKED };
  }
  const classified = classifyBoardPush({
    incomingAuthority: "dispatch",
    incomingDeskVersion: input.localDeskVersion,
    storedDeskVersion: input.remoteDeskVersion,
    incomingCompanyId: input.localCompanyId,
    storedCompanyId: input.remoteCompanyId,
    remoteHasBoard: input.remoteHasBoard,
  });
  if (classified === "allow") return { ok: true };
  return { ok: false, error: boardPushErrorMessage(classified) };
}

/**
 * Silent Pull when this desk is already on the Nanode company and the board/inbox hash moved.
 * Empty-local is Join’s job (do not steal the first-run wizard).
 * An unpublished local edit (local hash ≠ last applied) wins the tick — Push first.
 */
export function shouldAutoPullSchedule(input: {
  syncConfigured: boolean;
  companyKind: CompanySyncKind;
  remoteRevision: string;
  lastAppliedRevision: string;
  /** Current desk schedule hash. When it differs from last applied, skip Pull. */
  localRevision?: string;
  /** Local start-from-scratch wipe — do not Pull Nanode’s old board back. */
  holdRemoteBoard?: boolean;
  inboxPublisher?: boolean;
  boardSeat?: string | null;
  role?: string | null;
}): boolean {
  if (!input.syncConfigured) return false;
  if (input.holdRemoteBoard) return false;
  if (input.companyKind !== "same") return false;
  const remote = input.remoteRevision.trim();
  if (!remote) return false;
  const applied = input.lastAppliedRevision.trim();
  const local = (input.localRevision ?? "").trim();
  if (applied && local && local !== applied) {
    // Publisher may Push first. Followers keep Nanode — stale local history loses.
    if (boardAuthorityForDesk(input) !== "follower") return false;
  }
  return remote !== applied;
}

export function companySyncConflictPrompt(c: CompanySyncClassification): string {
  const remote = c.remoteName || "the Nanode company";
  const local = c.localName || "this desk";
  return [
    `This Nanode already holds ${remote} (${c.remoteId || "no id"}).`,
    `This PC is ${local} (${c.localId || "no id"}) — a different career, not a second seat on the same company.`,
    "",
    "OK = Pull & replace with the Nanode company (shared Inbox / board). This desk will follow; it will not mint its own auto-inbox.",
    "Cancel = keep this local career. Auto-Push stays off until you Join or Push snapshot to overwrite.",
  ].join("\n");
}

export type NanodeAuthoritySource = "company-snapshot" | "account-career" | "none";

/** Live `/v1/state` is the shared company. Account career is the fallback archive. */
export function pickNanodeAuthority(input: {
  snapshotAvailable: boolean;
  careerAvailable: boolean;
}): NanodeAuthoritySource {
  if (input.snapshotAvailable) return "company-snapshot";
  if (input.careerAvailable) return "account-career";
  return "none";
}

export function shouldAutoApplyNanodeOnLogin(source: NanodeAuthoritySource): boolean {
  return source !== "none";
}

/** Same Nanode account → take Nanode’s company. Do not prompt for a second career. */
export function shouldSilentTakeNanode(input: {
  companyKind: CompanySyncKind;
  accountSignedIn: boolean;
}): boolean {
  return input.accountSignedIn && (input.companyKind === "orphan" || input.companyKind === "empty-local");
}

export function companySyncPullNotice(exportedAtUtc: string): string {
  const when = exportedAtUtc.trim();
  return when ? `SYNC  pulled shared company board + inbox (${when})` : "SYNC  pulled shared company board + inbox";
}

export function remoteBoardStampMs(raw?: string | null): number | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * A successful Nanode Pull is sticky. An older career cache / dated snapshot
 * must not overwrite it when a later Pull times out (fail closed, keep local).
 */
export function shouldApplyRemoteBoard(input: {
  incomingAtUtc?: string | null;
  lastAppliedAtUtc?: string | null;
  allowOlder?: boolean;
}): boolean {
  if (input.allowOlder) return true;
  const incoming = remoteBoardStampMs(input.incomingAtUtc);
  const last = remoteBoardStampMs(input.lastAppliedAtUtc);
  if (incoming == null || last == null) return true;
  return incoming >= last;
}

/** Actionable company Inbox row — same set the dispatcher Inbox list works. */
export type LiveCompanyInboxRow = {
  status?: string;
  acceptedMissionId?: string;
  /** Duty Standby ring cards are phone-arm records, not charter RFQs. */
  kind?: string;
};

/**
 * Live company Inbox: new / assigned RFQs the dispatcher still works.
 * Declined rows and already-filed accepts stay in local persist for audit but
 * are not the board Megan, Book, or Push publish.
 */
export function isLiveCompanyInboxItem(row: LiveCompanyInboxRow | null | undefined): boolean {
  if (!row) return false;
  if (row.kind === "standby_call") return false;
  const status = typeof row.status === "string" ? row.status : "new";
  if (status === "declined") return false;
  if (status === "accepted" && String(row.acceptedMissionId ?? "").trim()) return false;
  return status === "new" || status === "assigned" || status === "accepted";
}

/** Same filter the dispatcher Inbox UI and snapshot serializer must share. */
export function liveCompanyInbox<T extends LiveCompanyInboxRow>(inbox: T[] | undefined | null): T[] {
  if (!Array.isArray(inbox)) return [];
  return inbox.filter(isLiveCompanyInboxItem);
}

/**
 * Push / PUT `/v1/state` is replace (last write wins). The incoming list is the
 * live company Inbox — omitted ids stay gone. Do not union leftover trickle /
 * demo rows from a previous snapshot; that resurrected a parallel 20-row board
 * after a 2-row career Push.
 */
export function mergeSharedInbox<T extends { id?: string; source?: string; status?: string }>(
  incoming: T[] | undefined,
  _existing?: T[] | undefined,
): T[] {
  return Array.isArray(incoming) ? incoming : [];
}

export type InboxPushClass = "allow" | "fatter-divergent";

export type LiveInboxIdRow = LiveCompanyInboxRow & { id?: string };

/** Live Inbox ids — same set Push packs and the dispatcher list shows. */
export function liveInboxIds(inbox: LiveInboxIdRow[] | undefined | null): string[] {
  return liveCompanyInbox(inbox).map((row) => String(row.id ?? "").trim()).filter(Boolean);
}

/**
 * Fetch growth is a superset (allow). Sim shrinking a leftover demo board is
 * thinner (allow). Book/demo overwriting ~2 ops rows with a fatter different
 * set is `fatter-divergent` — block silent Push; confirm to overwrite.
 */
export function classifyInboxPush(input: {
  localIds: string[];
  remoteIds?: string[] | null;
}): InboxPushClass {
  const local = new Set(input.localIds.map((id) => id.trim()).filter(Boolean));
  const remote = new Set((input.remoteIds ?? []).map((id) => id.trim()).filter(Boolean));
  if (remote.size === 0) return "allow";
  const remoteMissing = [...remote].filter((id) => !local.has(id));
  const localExtra = [...local].filter((id) => !remote.has(id));
  if (local.size > remote.size && localExtra.length > 0 && remoteMissing.length > 0) {
    return "fatter-divergent";
  }
  return "allow";
}

export function shouldQuietPushInbox(input: {
  inboxPublisher: boolean;
  localIds: string[];
  remoteIds?: string[] | null;
  /** Book/satellite publishing a real edit after applying Nanode. */
  syncedBoardMutation?: boolean;
  companyKind?: CompanySyncKind;
}): boolean {
  if (classifyInboxPush({ localIds: input.localIds, remoteIds: input.remoteIds }) === "fatter-divergent") {
    return false;
  }
  if (input.inboxPublisher) return true;
  return false;
}

export function pushOverwriteConfirmText(input: {
  localSummary: string;
  remoteInboxCount: number | null;
  remoteOpenTrips: number | null;
  risk: InboxPushClass;
}): string {
  const remoteLine =
    input.remoteInboxCount == null
      ? "Could not read Nanode inbox counts — confirm only on the sim ops desk."
      : `Nanode now: inbox ${input.remoteInboxCount}, open trips ${input.remoteOpenTrips ?? "—"}`;
  const riskLine =
    input.risk === "fatter-divergent"
      ? "This desk Inbox is larger and is not a superset of Nanode — typical Book/demo overwrite. Cancel unless this is the sim ops desk intentionally replacing the company board."
      : "v1 is replace (last write wins). A dated Nanode copy is kept.";
  return [
    "Overwrite the remote snapshot with this desk’s state?",
    "",
    input.localSummary.trim(),
    remoteLine,
    "",
    riskLine,
    "Book / satellite desks should Pull only. Sim is the Push desk.",
  ].join("\n");
}
